import { z } from 'zod';
import { EbpCasePatientSupportLevel } from '../../enums';
import { requiresUnique } from './settingsValidation';

// The support-level model (support.endDate + maxAppointments calculation,
// estimated discharge date, Level dropdown, Support Authorisation,
// end-of-support / no-future-booking notifications).
// What each level grants is supportLevelDefinitions, not this group.
// - levels: the levels the organization offers (e.g. Aviva: Level 1 only,
//   L&G: all three, Vitality: Clinical Service); kept (inert) while the
//   model is disabled, but an ENABLED model must offer at least one level.
// - defaultLevel: the level assigned to a NEW case at creation (postCase).
//   `null` = new cases start WITHOUT a level ("None") and the nurse selects
//   one manually — L&G. Aviva: "Level 1", its only offered level.
//   A non-null default must be one of the offered levels; the two fields
//   share this group doc so they always inherit atomically.
// - displayLevel: whether the level is shown anywhere — Level dropdown,
//   Support Authorisation label/modal titles, generated document content.
//   Aviva: false (PPL-3294 — the single pre-authorised level drives the
//   support machinery but is never referenced in the UI or documents).
//   Hiding is only meaningful when at most ONE level is offered (nothing to
//   choose); with multiple levels the choice must be visible, so
//   displayLevel is forced on.
// A present doc fully defines the group: all fields are required
// (the default is nullable).
export const SupportLevelsSettingsSchema = z
  .strictObject({
    enabled: z.boolean(),
    levels: z
      .array(z.enum(EbpCasePatientSupportLevel))
      .refine(...requiresUnique('levels')),
    defaultLevel: z.enum(EbpCasePatientSupportLevel).nullable(),
    displayLevel: z.boolean(),
  })
  .check((ctx) => {
    const { enabled, levels, defaultLevel, displayLevel } = ctx.value;

    if (enabled && levels.length === 0) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['levels'],
        message: 'levels must not be empty when enabled is on',
      });
    }

    if (defaultLevel !== null && !levels.includes(defaultLevel)) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['defaultLevel'],
        message: 'defaultLevel must be one of the offered levels',
      });
    }

    if (levels.length > 1 && !displayLevel) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['displayLevel'],
        message: 'displayLevel must be on when multiple levels are offered',
      });
    }
  });

export type SupportLevelsSettings = z.infer<typeof SupportLevelsSettingsSchema>;
